import type { FeedEntry } from '~/services/layout/index.ts';
import logger from '~/services/logger';
import type {
  AddAllToQueueOptions,
  FeedResolver,
  PlayerObserver,
  QueueManagerConfig,
  QueueStorageAdapter,
} from './types.ts';
import {
  findIndexById,
  injectQueueExtensions,
  matchesQueuedItem,
  reorderByIds,
} from './queue-utils.ts';

export const PLUGIN_IDENTIFIER = 'queue-action';
export const STORAGE_KEY = 'queue-items';
export const LAST_PLAYED_STORAGE_KEY = 'queue-last-played-id';
export const QUEUE_CHANGED_EVENT = 'queue.changed';

export class QueueManager {
  public currentProfileId: string | null = null;
  public state: FeedEntry[] = [];
  public isInitialized = false;
  public lastPlayedId: string | null = null;

  private resolvedUpNextItemIds = new Set<string>();
  private inFlightResolutions = new Map<string, Promise<void>>();
  private listeners = new Set<(state: FeedEntry[]) => void>();
  private storage: QueueStorageAdapter;
  public readonly feedResolver: FeedResolver;
  private playerObserver: PlayerObserver;
  private unsubscribePlayer: (() => void) | null = null;

  private static _instance: QueueManager | null = null;

  public static initialize(config: QueueManagerConfig): QueueManager {
    if (QueueManager._instance) {
      QueueManager._instance.destroy();
    }

    QueueManager._instance = new QueueManager(config);
    return QueueManager._instance;
  }

  public static isInitialized(): boolean {
    return QueueManager._instance !== null;
  }

  public static get instance(): QueueManager {
    if (!QueueManager._instance) {
      throw new Error(
        'QueueManager has not been initialized. Call QueueManager.initialize(config) first.'
      );
    }

    return QueueManager._instance;
  }

  public getStorageKey(key: string): string {
    return this.currentProfileId ? `${key}_${this.currentProfileId}` : key;
  }

  constructor(config: QueueManagerConfig) {
    if (!config) {
      throw new Error('QueueManager requires a config object.');
    }

    if (!config.storage) {
      throw new Error('QueueManager requires a storage adapter.');
    }

    if (!config.feedResolver) {
      throw new Error('QueueManager requires a feedResolver adapter.');
    }

    if (!config.playerObserver) {
      throw new Error('QueueManager requires a playerObserver adapter.');
    }

    this.storage = config.storage;
    this.feedResolver = config.feedResolver;
    this.playerObserver = config.playerObserver;

    this.initializeState = this.initializeState.bind(this);
    this.getState = this.getState.bind(this);
    this.isInQueue = this.isInQueue.bind(this);
    this.isLastElement = this.isLastElement.bind(this);
    this.addToQueue = this.addToQueue.bind(this);
    this.addAllToQueue = this.addAllToQueue.bind(this);
    this.removeFromQueue = this.removeFromQueue.bind(this);
    this.clearQueue = this.clearQueue.bind(this);
    this.getUpcomingQueue = this.getUpcomingQueue.bind(this);
    this.getHistoryQueue = this.getHistoryQueue.bind(this);
    this.onTrackStarted = this.onTrackStarted.bind(this);
    this.resolveUpNextFeed = this.resolveUpNextFeed.bind(this);
    this.addListener = this.addListener.bind(this);
    this.switchProfile = this.switchProfile.bind(this);
    this.setProfile = this.setProfile.bind(this);
    this.getActiveProfileId = this.getActiveProfileId.bind(this);

    this.unsubscribePlayer = this.playerObserver.subscribe(this.onTrackStarted);
  }

  public async switchProfile(profileId: string | null): Promise<void> {
    const pid = profileId ? String(profileId) : null;

    if (this.currentProfileId === pid && this.isInitialized) {
      return;
    }

    this.currentProfileId = pid;
    this.resolvedUpNextItemIds.clear();
    this.inFlightResolutions.clear();

    const stored = await this.storage.getItem<FeedEntry[]>(
      this.getStorageKey(STORAGE_KEY),
      PLUGIN_IDENTIFIER
    );

    const lastPlayed = await this.storage.getItem<string>(
      this.getStorageKey(LAST_PLAYED_STORAGE_KEY),
      PLUGIN_IDENTIFIER
    );

    this.state = Array.isArray(stored) ? stored : [];
    this.lastPlayedId = (lastPlayed as string) || null;
    this.isInitialized = true;
    this.notifyListeners();
  }

  public setProfile(profileId: string | null): Promise<void> {
    return this.switchProfile(profileId);
  }

  public getActiveProfileId(): string | null {
    return this.currentProfileId;
  }

  public destroy(): void {
    if (this.unsubscribePlayer) {
      this.unsubscribePlayer();
      this.unsubscribePlayer = null;
    }

    this.listeners.clear();
    this.inFlightResolutions.clear();
    this.resolvedUpNextItemIds.clear();
    this.state = [];
    this.lastPlayedId = null;
    this.isInitialized = false;
    this.currentProfileId = null;
    QueueManager._instance = null;
  }

  public addListener(listener: (state: FeedEntry[]) => void): () => void {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (e) {
        logger.error(`Error in QueueManager listener: ${e}`);
      }
    });

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(QUEUE_CHANGED_EVENT));
    }
  }

  private async persistState(): Promise<void> {
    await this.storage.setItem(
      this.getStorageKey(STORAGE_KEY),
      this.state,
      PLUGIN_IDENTIFIER
    );
  }

  async getState(profileId?: string | null): Promise<FeedEntry[]> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    return this.state;
  }

  public isLastElement(entry: FeedEntry): boolean {
    if (!entry || this.state.length === 0) return false;

    const lastItem = this.state[this.state.length - 1];

    if (entry.extensions?.queueItemId && lastItem.extensions?.queueItemId) {
      return (
        String(entry.extensions.queueItemId) ===
        String(lastItem.extensions.queueItemId)
      );
    }

    if (this.lastPlayedId) {
      const currentIndex = findIndexById(this.state, this.lastPlayedId);

      if (currentIndex !== -1) {
        return currentIndex === this.state.length - 1;
      }
    }

    return matchesQueuedItem(lastItem, entry);
  }

  public async resolveUpNextFeed(
    entry: FeedEntry,
    profileId?: string | null
  ): Promise<void> {
    if (!entry) return;

    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    const matchingItem = this.state.find((item) =>
      matchesQueuedItem(item, entry)
    );

    const targetEntry = matchingItem || entry;

    const slotId = String(
      targetEntry.extensions?.queueItemId ||
        entry.extensions?.queueItemId ||
        targetEntry.id ||
        entry.id
    );

    if (this.inFlightResolutions.has(slotId)) {
      return await this.inFlightResolutions.get(slotId);
    }

    if (this.resolvedUpNextItemIds.has(slotId)) {
      return;
    }

    const upNextFeed =
      targetEntry.extensions?.upNextFeed ||
      targetEntry.extensions?.up_next_feed ||
      entry.extensions?.upNextFeed ||
      entry.extensions?.up_next_feed;

    if (!upNextFeed) {
      return;
    }

    const resolutionPromise = (async () => {
      try {
        const newEntries = await this.feedResolver(upNextFeed, targetEntry);

        if (newEntries && newEntries.length > 0) {
          await this.addAllToQueue(newEntries);
          this.resolvedUpNextItemIds.add(slotId);
        }
      } catch (error) {
        logger.error(
          `Unexpected error resolving upNextFeed for slot ${slotId}: ${error}`
        );
      } finally {
        this.inFlightResolutions.delete(slotId);
      }
    })();

    this.inFlightResolutions.set(slotId, resolutionPromise);
    await resolutionPromise;
  }

  public async onTrackStarted(currentEntry: FeedEntry): Promise<void> {
    if (!currentEntry) return;

    if (!this.isInitialized) {
      await this.initializeState();
    }

    if (this.isInQueue(currentEntry)) {
      const matchingItem = this.state.find((item) =>
        matchesQueuedItem(item, currentEntry)
      );

      const queueItemId = String(
        matchingItem?.extensions?.queueItemId ||
          currentEntry.extensions?.queueItemId ||
          matchingItem?.id ||
          currentEntry.id
      );

      if (this.lastPlayedId !== queueItemId) {
        this.lastPlayedId = queueItemId;

        await this.storage.setItem(
          this.getStorageKey(LAST_PLAYED_STORAGE_KEY),
          queueItemId,
          PLUGIN_IDENTIFIER
        );

        this.notifyListeners();
      }

      if (this.isLastElement(currentEntry)) {
        await this.resolveUpNextFeed(currentEntry);
      }
    }
  }

  async getUpcomingQueue(profileId?: string | null): Promise<FeedEntry[]> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    let currentIndex = -1;

    if (this.lastPlayedId) {
      currentIndex = findIndexById(this.state, this.lastPlayedId);
    }

    if (currentIndex !== -1 && currentIndex === this.state.length - 1) {
      await this.resolveUpNextFeed(this.state[currentIndex]);
      return this.state.slice(currentIndex + 1);
    }

    if (currentIndex !== -1) {
      return this.state.slice(currentIndex + 1);
    }

    return this.state;
  }

  async getHistoryQueue(profileId?: string | null): Promise<FeedEntry[]> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    if (this.lastPlayedId) {
      const currentIndex = findIndexById(this.state, this.lastPlayedId);

      if (currentIndex !== -1) {
        return this.state.slice(0, currentIndex);
      }
    }

    return [];
  }

  private initPromise: Promise<void> | null = null;

  async initializeState(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = (async () => {
      try {
        const stored = await this.storage.getItem<FeedEntry[]>(
          this.getStorageKey(STORAGE_KEY),
          PLUGIN_IDENTIFIER
        );

        const lastPlayed = await this.storage.getItem<string>(
          this.getStorageKey(LAST_PLAYED_STORAGE_KEY),
          PLUGIN_IDENTIFIER
        );

        this.lastPlayedId = (lastPlayed as string) || null;
        this.state = Array.isArray(stored) ? stored : [];
      } catch (e) {
        this.state = [];
        this.lastPlayedId = null;
      } finally {
        this.isInitialized = true;
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  isInQueue(entry: FeedEntry): boolean {
    if (!entry) return false;
    return this.state.some((item) => matchesQueuedItem(item, entry));
  }

  async addToQueue(entry: FeedEntry, profileId?: string | null): Promise<void> {
    if (!entry?.id) return;

    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    this.state = [...this.state, injectQueueExtensions(entry)];

    await this.persistState();

    this.notifyListeners();
  }

  async removeFromQueue(
    entry: FeedEntry,
    profileId?: string | null
  ): Promise<void> {
    const queueItemId = entry?.extensions?.queueItemId;

    if (!entry?.id && !queueItemId) return;

    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    if (queueItemId) {
      this.resolvedUpNextItemIds.delete(String(queueItemId));
      this.state = this.state.filter((item) => !matchesQueuedItem(item, entry));
    } else {
      const index = this.state.findIndex((item) =>
        matchesQueuedItem(item, entry)
      );

      if (index !== -1) {
        const removedItem = this.state[index];
        const removedSlotId = removedItem.extensions?.queueItemId;

        if (removedSlotId) {
          this.resolvedUpNextItemIds.delete(String(removedSlotId));
        }

        this.state = this.state.filter((_, itemIndex) => itemIndex !== index);
      }
    }

    await this.persistState();

    this.notifyListeners();
  }

  async addAllToQueue(
    entries: FeedEntry[],
    options?: AddAllToQueueOptions,
    profileId?: string | null
  ): Promise<FeedEntry | null> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    const newEntries = (entries || [])
      .filter((entry) => entry?.id)
      .map((entry) => injectQueueExtensions(entry));

    if (newEntries.length === 0) {
      return null;
    }

    const position = options?.position || 'bottom';
    const startPlayback = options?.startPlayback === true;

    if (position === 'top') {
      let insertIndex = 0;

      if (this.lastPlayedId) {
        const currentIndex = this.state.findIndex(
          (item) =>
            String(item.extensions?.queueItemId) ===
              String(this.lastPlayedId) ||
            String(item.id) === String(this.lastPlayedId)
        );

        if (currentIndex !== -1) {
          insertIndex = currentIndex + 1;
        }
      }

      this.state = [
        ...this.state.slice(0, insertIndex),
        ...newEntries,
        ...this.state.slice(insertIndex),
      ];
    } else {
      this.state = [...this.state, ...newEntries];
    }

    let firstEntry: FeedEntry | null = null;

    if (startPlayback && newEntries.length > 0) {
      firstEntry = newEntries[0];
      const queueItemId = String(firstEntry.extensions?.queueItemId);
      this.lastPlayedId = queueItemId;

      await this.storage.setItem(
        this.getStorageKey(LAST_PLAYED_STORAGE_KEY),
        queueItemId,
        PLUGIN_IDENTIFIER
      );
    }

    await this.persistState();

    this.notifyListeners();

    return firstEntry;
  }

  async clearQueue(profileId?: string | null): Promise<void> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    this.state = [];
    this.lastPlayedId = null;
    this.resolvedUpNextItemIds.clear();
    this.inFlightResolutions.clear();

    await this.persistState();

    await this.storage.setItem(
      this.getStorageKey(LAST_PLAYED_STORAGE_KEY),
      '',
      PLUGIN_IDENTIFIER
    );

    this.notifyListeners();
  }

  async move(itemIds: string[], profileId?: string | null): Promise<void> {
    if (profileId !== undefined && profileId !== this.currentProfileId) {
      await this.switchProfile(profileId);
    }

    if (!this.isInitialized) {
      await this.initializeState();
    }

    if (itemIds && itemIds.length > 0) {
      const idSelector = (item: FeedEntry) => {
        const queueItemId = item.extensions?.queueItemId;

        if (queueItemId && itemIds.includes(String(queueItemId))) {
          return String(queueItemId);
        }

        return String(item.id);
      };

      this.state = reorderByIds(this.state, itemIds, idSelector);

      await this.persistState();

      this.notifyListeners();
    }
  }
}
